import { z } from 'zod';

export const FieldPattern = z.enum(['closed_enum', 'open_enum', 'free_text']);

export const FieldDefinition = z
  .object({
    id: z.string(),
    label: z.string(),
    pattern: FieldPattern,
    catalogId: z.string().optional(),
    required: z.boolean().optional(),
    description: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.pattern !== 'free_text' && !value.catalogId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'catalogId obbligatorio per pattern enum',
        path: ['catalogId'],
      });
    }
  });

export const FieldValue = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('option'),
    optionId: z.string(),
  }),
  z.object({
    kind: z.literal('custom'),
    text: z.string(),
  }),
  z.object({
    kind: z.literal('text'),
    text: z.string(),
  }),
]);

const normalizeLegacyText = (value: string): string => value.trim().replace(/\s+/g, ' ');

const isFieldValueLike = (value: unknown): value is FieldValue =>
  typeof value === 'object' &&
  value !== null &&
  'kind' in value &&
  typeof (value as { kind?: unknown }).kind === 'string';

export function coerceLegacyFieldValue(
  value: unknown,
  pattern: FieldPattern
): FieldValue | null | undefined {
  if (value === null || value === undefined) return value;
  if (isFieldValueLike(value)) return value;
  if (typeof value !== 'string') return value as FieldValue;

  const text = normalizeLegacyText(value);
  if (!text) return undefined;

  if (pattern === 'closed_enum') {
    return { kind: 'option', optionId: text };
  }

  if (pattern === 'free_text') {
    return { kind: 'text', text };
  }

  return { kind: 'custom', text };
}

export type FieldPattern = z.infer<typeof FieldPattern>;
export type FieldDefinition = z.infer<typeof FieldDefinition>;
export type FieldValue = z.infer<typeof FieldValue>;
